import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  Signal,
  signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TaskFiltersService } from '@app/components/task/task-list-toolbar/task-filters.service';
import { TaskPrioritySelectComponent } from '@app/components/task/task-priority-select/task-priority-select.component';
import { TaskRowActionsMenuComponent } from '@app/components/task/task-row-actions-menu/task-row-actions-menu.component';
import { LocationSelectComponent } from '@app/components/task/task-state-selectors/location-select/location-select.component';
import { TaskStatusSelectComponent } from '@app/components/task/task-state-selectors/task-status-select/task-status-select.component';
import { TaskTypeSelectComponent } from '@app/components/task/task-state-selectors/task-type-select/task-type-select.component';
import { AvatarsGroupComponent } from '@app/shared/components/group-avatars/proffeo-group-avatars.component';
import { TagComponent } from '@app/shared/components/tag/tag.component';
import { formatHtmlToTextPipe } from '@app/shared/pipes/formatHtmlToText.pipe';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { TaskParticipantsDataService } from '@app/shared/services/task-participants-data.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TASK_STATUS_DISPLAY } from '@app/shared/types/enums/task-status.constants';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { Tag } from '@app/shared/types/models/tag/tag.model';
import { TaskStatusSelectEnum } from '@app/shared/types/models/task/task-status.model';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDatePickerCompactComponent } from '@app/shared/ui/date-picker-compact/ui-date-picker-compact.component';
import { UiPriorityBadgeComponent } from '@app/shared/ui/priority-badge/ui-priority-badge.component';
import { UiStatusBadgeComponent } from '@app/shared/ui/status-badge/ui-status-badge.component';
import { UiTaskAssigneesComponent } from '@app/shared/ui/task-assignees/ui-task-assignees.component';
import { formatPercentageValue } from '@app/shared/utils/percentage-format.util';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TranslatePipe } from '@ngx-translate/core';
import { TaskIndustrySelectComponent } from '../../task-state-selectors/task-industry-select/task-industry-select.component';
import { TaskTimerComponent, TaskTimerMode } from '../../task-state-selectors/task-timer/task-timer.component';

@Component({
  selector: 'proffeo-task-splitview-list-item',
  templateUrl: './task-splitview-list-item.component.html',
  styleUrl: './task-splitview-list-item.component.scss',
  imports: [
    DatePipe,
    MatIconModule,
    AvatarsGroupComponent,
    UiButtonComponent,
    TranslatePipe,
    UiStatusBadgeComponent,
    UiPriorityBadgeComponent,
    TaskStatusSelectComponent,
    TaskPrioritySelectComponent,
    TaskTypeSelectComponent,
    UiDatePickerCompactComponent,
    TaskIndustrySelectComponent,
    LocationSelectComponent,
    UiTaskAssigneesComponent,
    TaskTimerComponent,
    formatHtmlToTextPipe,
    TagComponent,
    TaskRowActionsMenuComponent
  ]
})
export class TaskSplitviewListItemComponent {
  private readonly tasksService: TasksDataService = inject(TasksDataService);
  private readonly taskParticipantsService = inject(TaskParticipantsDataService);
  private readonly tagsService: TagsDataService = inject(TagsDataService);
  private readonly activeTaskService: ActiveTaskService = inject(ActiveTaskService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly taskActionsService = inject(TasksActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly taskFilters = inject(TaskFiltersService, { optional: true });
  private readonly childrenResult = signal<Signal<{ status: 'loading' | 'loaded' | 'error'; tasks: Task[] }> | null>(
    null
  );
  private readonly expandedTaskRequest = computed(
    () => {
      const task = this.task();
      const projectId = task.project()?.id;
      const taskId = task.id;
      if (!projectId || !taskId || !this.expandedNodes().has(taskId)) {
        return null;
      }

      return { projectId, taskId };
    },
    {
      equal: (previous, current) => previous?.projectId === current?.projectId && previous?.taskId === current?.taskId
    }
  );

  protected readonly subtasks = computed(() => this.childrenResult()?.().tasks ?? []);
  protected readonly childrenStatus = computed(() => this.childrenResult()?.().status);
  protected readonly taskChildrenId = computed(() => `task-children-${this.task().id}`);

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly tags: Signal<Tag[]> = computed(() => this.tagsService.getTaskTags(this.task().id)());
  protected readonly TaskStatusSelectEnum = TaskStatusSelectEnum;
  protected readonly TaskPriority = TaskPriority;
  protected readonly TASK_STATUS_DISPLAY = TASK_STATUS_DISPLAY;

  protected readonly locationDisplayPath: Signal<string> = computed(() => {
    const location = this.task().location?.();
    if (!location) {
      return '';
    }

    return location.locationsPath
      .map(segment => segment.name)
      .filter((name): name is string => !!name)
      .join(' > ');
  });

  protected readonly industryName: Signal<string> = computed(
    () => this.task().projectIndustry()?.industry()?.name ?? ''
  );
  protected readonly mobileAvatarMode = computed(() => this.taskFilters?.mobileAvatarMode() ?? 'task');
  protected readonly mobileDateMode = computed(() => this.taskFilters?.mobileDateMode() ?? 'none');
  protected readonly mobileStatusVisible = computed(() => this.taskFilters?.mobileStatusVisible() ?? true);
  protected readonly mobilePriorityVisible = computed(() => this.taskFilters?.mobilePriorityVisible() ?? true);
  protected readonly visibleMobileDate = computed(() =>
    this.mobileDateMode() === 'start' ? this.task().startDate : this.task().endDate
  );
  protected readonly mobileDateLabelKey = computed(() =>
    this.mobileDateMode() === 'start'
      ? 'project-tasks.list.column-headers.start-date'
      : 'project-tasks.list.column-headers.end-date'
  );
  protected readonly cardStatusBorder = computed(() =>
    this.isMobile() && !this.mobileStatusVisible()
      ? ''
      : `${TASK_STATUS_DISPLAY[this.task().status].borderLeftClass} border-l-[6px]!`
  );

  protected readonly formatPercent: (percentageValue: number) => string = formatPercentageValue;

  protected readonly mobileIndent = computed(() => Math.min(this.subtaskDepth(), 3) * 12);
  protected calculatedIndentWidth: Signal<string> = computed(() => `${this.subtaskDepth() * 10}px`);
  protected imgError: WritableSignal<boolean> = signal(false);
  protected avatarUrl = computed(() => this.task()?.getAvatarUrl()());
  protected taskUsers = computed(
    () =>
      this.taskParticipantsService
        .getTaskParticipants(this.task().id)()
        ?.map(tp => tp.projectParticipant()) || []
  );
  protected participantUsers = computed(() => this.taskUsers().map(participant => participant.user()));
  protected AvatarSize = AvatarSize;
  protected taskTimerMode = TaskTimerMode;
  protected isExpanded: Signal<boolean> = computed(() => this.expandedNodes().has(this.task()?.id ?? ''));
  protected shouldRenderChildren: Signal<boolean> = computed(() => this.isExpanded());
  /** @summary Opt-in mobile table-equivalent layout; cards remain available. */
  public readonly mobileCompact = input(false);
  public task: InputSignal<Task> = input.required<Task>();
  public compactMode: InputSignal<boolean> = input(true);
  public hasChildren: InputSignal<boolean> = input(false);
  public isSubtask: InputSignal<boolean> = input(false);
  public taskTypes: InputSignal<TaskType[]> = input.required<TaskType[]>();
  public expandedNodes: ModelSignal<Set<string>> = model.required<Set<string>>();
  public isSelected: Signal<boolean> = computed(() => this.activeTaskService.activeTaskId() === this.task()?.id);
  public subtaskDepth: InputSignal<number> = input<number>(0);
  public statusChanged = output<{ status: TaskStatus; task: Task }>();
  public priorityChanged = output<{ priority: TaskPriority; task: Task }>();
  public taskStartDateSelected = output<{ date: Date; task: Task }>();
  public taskEndDateSelected = output<{ date: Date; task: Task }>();
  public taskProgressSelected = output<{ progress: number; task: Task }>();
  public industryClicked = output<Task>();
  public locationClicked = output<Task>();
  public changeGroupClicked = output<Task>();
  public changeUsersClicked = output<Task>();

  public constructor() {
    effect(() => {
      const request = this.expandedTaskRequest();
      if (!request) {
        return;
      }

      // Request once on explicit expansion; response/error changes must not trigger retries.
      this.childrenResult.set(
        untracked(() => this.tasksService.getTasksByParentResult(request.projectId, request.taskId))
      );
    });
  }

  protected retryChildren(): void {
    const task = this.task();
    const projectId = task.project()?.id;
    if (projectId && this.isExpanded()) {
      this.childrenResult.set(this.tasksService.getTasksByParentResult(projectId, task.id));
    }
  }

  protected onLockTask(): void {
    this.taskActionsService.lockTask(this.task());
  }

  protected onUnlockTask(): void {
    this.taskActionsService.unlockTask(this.task());
  }

  protected onDeleteTask(): void {
    this.taskActionsService.deleteTask(this.task()).subscribe();
  }

  protected onTaskClick(): void {
    void this.activeTaskService.setActiveTask(this.task());
  }

  protected toggleSubtasks(event: Event): void {
    event.stopPropagation();
    const taskId = this.task()?.id;
    if (!taskId) {
      return;
    }

    this.expandedNodes.update(currentExpandedNodes => {
      const nextExpandedNodes = new Set(currentExpandedNodes);
      if (nextExpandedNodes.has(taskId)) {
        nextExpandedNodes.delete(taskId);
      } else {
        nextExpandedNodes.add(taskId);
      }
      return nextExpandedNodes;
    });
  }

  protected onAvatarError(): void {
    this.imgError.set(true);
  }

  protected onCopyLink(): void {
    this.taskActionsService.copyTaskLink(this.task());
  }

  protected onCopyTaskNumber(): void {
    this.taskActionsService.copyTaskNumber(this.task());
  }

  protected onCopyTask(): void {
    this.taskActionsService.copyTask(this.task());
  }

  protected onGeneratePdf(): void {
    this.dialogService.openGenerateTaskPdfDialog(this.task());
  }

  protected emitTaskEndDateSelected(date: Date | null): void {
    if (date) {
      this.taskEndDateSelected.emit({ date, task: this.task() });
    }
  }

  protected onIndustryClicked(task: Task): void {
    this.industryClicked.emit(task);
  }
}
